import { pushApi } from '../api/push';

/** Web Push subscription helpers.
 *
 *  Permission UX lives in components (banner/settings) — this file
 *  is the plumbing those components call into. */

const SW_PATH = '/sw.js';

/** True when the browser supports the APIs we need. Safari technically
 *  supports web push on macOS 13+ via a different code path, but for
 *  v1 we limit to the standard. */
export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Register the service worker. Idempotent — browsers dedupe by URL.
 *  Safe to call on every app start. Returns the registration so
 *  callers can subscribe. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  try {
    return await navigator.serviceWorker.register(SW_PATH);
  } catch {
    return null;
  }
}

/** Subscribe the current browser to push.
 *
 *  Flow:
 *   1. Ensure SW is registered
 *   2. Ensure Notification permission is granted (will prompt if not)
 *   3. Pull VAPID public key from backend
 *   4. Call pushManager.subscribe → get subscription
 *   5. POST keys to backend
 *
 *  Returns true on success, false on denial / unsupported / error.
 *  Caller decides how to surface that (toast, banner, settings UI). */
export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) return false;

  const registration = await registerServiceWorker();
  if (!registration) return false;

  // Permission gate. If denied previously, requesting again is a
  // no-op in modern browsers — user has to flip it in settings.
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return false;

  let vapidKey: string;
  try {
    vapidKey = await pushApi.vapidPublicKey();
  } catch {
    return false;
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    } catch {
      return false;
    }
  }

  const { endpoint } = subscription;
  const keys = subscription.toJSON().keys ?? {};
  if (!keys.p256dh || !keys.auth) return false;

  try {
    await pushApi.subscribe({
      endpoint,
      p256dh: keys.p256dh,
      auth: keys.auth,
    });
    return true;
  } catch {
    return false;
  }
}

/** Unsubscribe from push: cancel server-side first, then locally so
 *  if the backend call fails the user can retry without the browser
 *  having dropped state. */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!registration) return true;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return true;

  try {
    await pushApi.unsubscribe(subscription.endpoint);
  } catch {
    // Continue with local unsub even on backend failure — we'd
    // rather over-clean and re-register than leave the browser
    // receiving pushes it can't process.
  }
  return subscription.unsubscribe();
}

export async function getCurrentSubscriptionStatus(): Promise<
  'subscribed' | 'denied' | 'available' | 'unsupported'
> {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!registration) return 'available';
  const sub = await registration.pushManager.getSubscription();
  return sub ? 'subscribed' : 'available';
}

/** Convert the VAPID public key from base64-url to the Uint8Array
 *  that pushManager.subscribe expects. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
